import { FC, useEffect, useRef } from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Grid,
  Divider,
  Button,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  ButtonGroup,
  Box
} from '@material-ui/core';
import { InputField } from '../input-field';
import { useDispatch, useSelector } from "react-redux";
import { ConfirmationDialog } from '../confirmation-dialog';
import { movePost, resetMoveStatus, updateField, moveDelete, addSelectedConcerning, removeSelectedConcerning, clearSelectedConcernings } from '../../slices/moveSlice';
import { review4GetAll } from '../../slices/review4Slice';
import { review3GetAll } from '../../slices/review3Slice';
import { unknownGetAll } from '../../slices/unknownSlice';
import { RootState } from '../../store';
import { useLocation } from 'react-router-dom';
import { getActiveElement } from '../../utils/active-element';
import { Wizard } from './move-wizard';
import { useTranslation } from 'react-i18next';
import { InfoPop } from '../info-pop';
import { openMoveDialog } from 'src/slices/moveSlice';
import { ChevronLeft as ChevronLeftIcon } from '../../icons/chevron-left';
import { ChevronRight as ChevronRightIcon } from '../../icons/chevron-right';
import { moveGetAll } from 'src/api/move';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSquareCheck, faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { TableItemMenu } from '../table-item-menu';
import MoveConcerningsPanel from './move-concernings-panel';
import { concernings, filterActiveConcernings } from '../../utils/concernings';
import { customerGetAll } from '../../api/review-1/review-1-customer';
import { companyGetAll } from '../../api/review-1/review-1-company';

interface MoveDialogProps {
  open: boolean;
  onClose: () => void;
  /** Offset the dialog when it opens stacked over another dialog (Unknown). */
  stacked?: boolean;
}

export const MoveDialog: FC<MoveDialogProps> = (props) => {
  const { t } = useTranslation();
  const { open, onClose, stacked, ...other } = props;
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const move = useSelector((state: RootState) => state.move);
  console.log("MODAL MOVE", move);
  const dispatch = useDispatch();
  const { moveIsLoading, moveStatus, moveNextAction, selectedConcernings } = useSelector((state: RootState) => state.move);

  // const {moves} = useSelector((state: RootState) => state.review4);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchedString, setSearchedString] = useState('');

  const [showList, setShowList] = useState(true);
  const [showConcerningsPanel, setShowConcerningsPanel] = useState(false);
  // Current wizard step — Comment & Concerning are only shown on the first page.
  const [wizardStep, setWizardStep] = useState(0);
  const [height, setHeight] = useState(0)
  const mainGridRef = useRef(null)
  const [refVisible, setRefVisible] = useState(false)
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!refVisible) {
      return
    }
    setHeight(mainGridRef.current.clientHeight)
  }, [refVisible, showDetails, mainGridRef?.current?.clientHeight])

  useEffect(() => {
    getItems();
  }, []);

  useEffect(() => {
    getItems();
  }, [searchedString]);

  // Load clients and companies for chip display
  useEffect(() => {
    const loadClientsAndCompanies = async () => {
      try {
        const [clientsData, companiesData] = await Promise.all([
          customerGetAll(),
          companyGetAll()
        ]);

        if (clientsData !== 'error') {
          setClients(clientsData || []);
        }

        if (companiesData && !companiesData.err) {
          setCompanies(companiesData.items || []);
        }

        setDataLoaded(true);
      } catch (error) {
        console.error('Error loading clients and companies:', error);
        setDataLoaded(true);
      }
    };

    loadClientsAndCompanies();
  }, []);

  const getItems = async () => {
    const res = await moveGetAll('name', 'asc', searchedString, 'all', 1, 1000);
    if (res.items) {
      setItems(res.items);
      setTotal(res.total);
    } else {
      setItems([]);
      setTotal(0);
    }
  };

  const location = useLocation();
  const activeElement = getActiveElement(location.pathname);

  // Function to get display name for a concerning
  const getConcerningDisplayName = (concerningId: string | any) => {
    // Handle case where concerningId is an object (from API)
    if (typeof concerningId === 'object' && concerningId !== null) {
      // If it's an object with a value property, use that
      if (concerningId.value) {
        return t(concernings[concerningId.value]?.label || concerningId.value);
      }
      // If it's an object with an id property, convert to string
      if (concerningId.id) {
        concerningId = concerningId.id.toString();
      } else {
        // Fallback for unknown object structure
        return t('Unknown concerning');
      }
    }

    // Ensure concerningId is a string
    const concerningIdStr = String(concerningId);

    // Check if it's a client
    if (concerningIdStr.startsWith('clients_')) {
      const clientId = concerningIdStr.replace('clients_', '');
      const client = clients.find(c => c.id == clientId || c.id === parseInt(clientId));
      return client ? (client.name || client.description || `Client ${clientId}`) : concerningIdStr;
    }

    // Check if it's a company
    if (concerningIdStr.startsWith('companies_')) {
      const companyId = concerningIdStr.replace('companies_', '');
      const company = companies.find(c => c.id == companyId || c.id === parseInt(companyId));
      return company ? (company.name || company.description || `Company ${companyId}`) : concerningIdStr;
    }

    // For static concernings, use the concernings mapping
    return t(concernings[concerningIdStr]?.label || concerningIdStr);
  };

  useEffect(() => {
    console.log('store status', moveIsLoading);
    if (moveStatus === 'success') {
      toast.success('Action created');
      dispatch(review4GetAll());
      dispatch(review3GetAll());
      dispatch(unknownGetAll());
      dispatch(resetMoveStatus());
      getItems(); // Refresh the actions list in the right panel
      if (moveNextAction === 'close') {
        onClose();
      } else {
        // "Save and create new": reset the dialog to a blank new action.
        dispatch(openMoveDialog({ element: { id: null }, isEditabled: true, isDuplicated: false }));
      }
    }
  }, [moveStatus]);

  const handleSubmit = (nextAction = 'close') => {
    console.log('Submit', nextAction);
    let values = {
      name: move.name,
      comment: move.comment,
      selectedForActionPlan: move.selectedForActionPlan,
      launchPlan: move.launchPlan,
      reduceUnknownPlan: move.reduceUnknownPlan,
      decisionForActionPlan: move.decisionForActionPlan,
      concerning: move.concerning,
      collateralEffect: move.collateralEffect,
      startDate: move.startDate,
      finishDate: move.finishDate,
      estimatedCost: move.estimatedCost,
      descriptionOfTheMove: move.descriptionOfTheMove,
      kpi: move.kpi,
      goalDate: move.goalDate,
      descriptionOfTheGoal: move.descriptionOfTheGoal,
      costing: move.costing,
      detailedImpact: move.detailedImpact,
      concernings: selectedConcernings.map(concerning => {
        // If concerning is already an object with id and value properties, use it as-is
        if (typeof concerning === 'object' && concerning !== null && concerning.id && concerning.value) {
          return concerning;
        }
        // Otherwise, wrap it in the expected format
        return {
          value: concerning,
          id: concerning
        };
      })
    };
    console.log('new stakeholder', values);
    dispatch(movePost(nextAction, values, move.moveId));
  };

  return (
    <Dialog
      maxWidth="xl"
      fullWidth
      onClose={() => { move.name !== '' ? setOpenConfirmationDialog(true) : onClose(); }}
      open={open}
      // TransitionProps={{
      //   onExited: () => formik.resetForm()
      // }}
      PaperProps={{
        // When stacked over the Unknown dialog, shift right + up so the left
        // part of the Unknown window stays partly visible behind it. Centered
        // as usual when opened on its own.
        style: stacked
          ? { borderRadius: 25, transform: 'translate(56px, -40px)' }
          : { borderRadius: 25 }
      }}
      {...other}
    >
      {/* <DialogTitle>
        {(move.moveId !== null && move.moveId !== undefined) ? `${t('update_move')} : ${move.name}` : t('create_move')}
        <div style={{ display: 'inline-block'}}>
          <InfoPop
            infoElementName={t('moves')}
            shortText='What is a Tactical Action?'
            position='right'
            longText='A Tactical Action is an action taken to reduce Unknowns or to implement the Solution/product. An Action can be selected to contribute to the Action Plan, to the Implementation Plan or to the Plan to reduce Unknowns. For each Action you should clarify its objective/purpose, its timeline, its cost, the resource requirements, its impact on Stakeholders, and potential collateral effects'
          />
        </div>
      </DialogTitle> */}
      <DialogContent sx={{ padding: 0, backgroundColor: "background.default" }}>
        <Grid
          container
          spacing={2}
        // my={2}
        >
          <Grid item xs={showList ? 8 : 12} sx={{ p: 0, backgroundColor: "background.default" }}>
            <Grid container spacing={2} padding={4} sx={{ overflowY: "scroll" }} maxHeight={'80vh'} ref={el => { mainGridRef.current = el; setRefVisible(!!el); }}>
              <Grid item xs={12}>
                <Typography variant="h5" color={'success.main'}>
                  {(move.moveId !== null && move.moveId !== undefined) ? `${t('update_move')} : ${move.name}` : t('create_move')}
                  {/* <div style={{ display: 'inline-block'}}>
                    <InfoPop
                      infoElementName={t('unknowns')}
                      shortText='What is a Unknown?'
                      position='right'
                      longText='Unknowns exist when information or knowledge are missing. Once the missing information has been obtained, the Unknown no longer exists. Since Unknowns are only testifying of your ignorance, they have no probability of occurrence like it is the case for Risks.<br />
                      Unknowns can relate to Customers, technology, market, regulations, Stakeholders, organizational issues,Resources, etc.<br />
                      Once Unknowns have been identified, you can figure out the effort (time, money, people, etc.) needed to obtain the missing information/knowledge, which will eventually reduce the Unknowns. You should make sure to reduce Unknowns in the optimal sequence that consumes the least amount of resources (money, time, etc.). <br />
                      To optimize the sequence you should establis an as complete as possible inventory of all identifiable Unknowns. <br />
                      Any Unknown that has not been identified reduces your ability to realistically assess the effort required to reduce Unknowns and/or deliver the Definition of Success.'
                    />
                  </div> */}
                  <div style={{ display: 'inline-block', textAlign: 'right', float: 'right' }}>
                    <IconButton onClick={() => setShowList(!showList)}>
                      {showList ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                  </div>
                </Typography>
              </Grid>
              {/* Concernings Section — shown only on the first page */}
              {wizardStep === 0 && (
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1
                }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t('Concerning')}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowConcerningsPanel(!showConcerningsPanel)}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem'
                    }}
                  >
                    {showConcerningsPanel ? t('Hide') : t('Add')}
                  </Button>
                </Box>

                {/* Concernings Summary */}
                <Box sx={{
                  minHeight: 40,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1.5,
                  backgroundColor: '#fafafa'
                }}>
                  {!dataLoaded ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {t('Loading concernings...')}
                    </Typography>
                  ) : filterActiveConcernings(selectedConcernings, clients, companies).length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {filterActiveConcernings(selectedConcernings, clients, companies).map((concerning, index) => (
                        <Chip
                          key={index}
                          label={getConcerningDisplayName(concerning)}
                          size="small"
                          color="success"
                          variant="outlined"
                          onDelete={() => dispatch(removeSelectedConcerning(concerning))}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {t('No concernings selected')}
                    </Typography>
                  )}
                </Box>
              </Grid>
              )}

              <Grid item xs={12}>
                <InputField
                  autoFocus
                  fullWidth
                  label={t('action_name')}
                  name="name"
                  // onBlur={formik.handleBlur}
                  onChange={(e) => dispatch(updateField({ field: 'name', value: e.target.value }))}
                  required
                  type="name"
                  value={move.name}
                  multiline
                />
              </Grid>
              {wizardStep === 0 && (
              <Grid item xs={12}>
                <InputField
                  fullWidth
                  label={t('comment_about_action')}
                  name="comment"
                  // onBlur={formik.handleBlur}
                  onChange={(e) => dispatch(updateField({ field: 'comment', value: e.target.value }))}
                  type="comment"
                  value={move.comment}
                  multiline
                  rows={2}
                />
              </Grid>
              )}

              {/* <Grid item xs={12} textAlign="center">
                <Button
                  onClick={() => setShowDetails(!showDetails)}
                  type="button"
                  color='success'
                >
                  {showDetails === true ? t('hide_details') : t('show_details')}
                </Button>
              </Grid> */}
              {/* {showDetails === true ? (
                <Grid item xs={12}>
                  <Wizard data={move} />
                </Grid>
              ) : (<></>)} */}
              <Grid item xs={12}>
                <Wizard data={move} onStepChange={setWizardStep} />
              </Grid>
              <Grid><Divider /></Grid>
              <Grid item xs={12} textAlign="center">
                <Button
                  color="success"
                  size="small"
                  onClick={onClose}
                  variant="outlined"
                  sx={{ mx: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  color="success"
                  size="small"
                  disabled={(move.moveIsLoading || !move.isEditabled || (move.name.length < 3))}
                  onClick={() => { handleSubmit('new'); }}
                  variant="outlined"
                  sx={{ mx: 1 }}
                >
                  {t('save_and_new')}
                </Button>
                <Button
                  color="success"
                  size="small"
                  disabled={(move.moveIsLoading || !move.isEditabled || (move.name.length < 3))}
                  onClick={() => { handleSubmit('close'); }}
                  variant="contained"
                  sx={{ mx: 1 }}
                >
                  {(move.moveId !== null && move.moveId !== undefined) ? t('update') : t('create')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          {showList && !showConcerningsPanel && (
            <Grid item xs={4} sx={{ backgroundColor: "rgba(39, 171, 109, 0.25)", overflowY: "scroll" }} padding={0} spacing={2} height={height}>
              <Grid container spacing={2} padding={4} sx={{ paddingLeft: 2 }}>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                  <Typography variant="h5" style={{ color: "rgb(39, 171, 109)" }}>
                    All actions <Chip color='default' label={total} size='small' variant='filled' />
                  </Typography>
                </Grid>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                  <Button variant='contained' size='small' fullWidth onClick={() => dispatch(openMoveDialog({ element: { id: null }, isEditabled: true, isDuplicated: false }))} style={{ backgroundColor: 'rgb(39, 171, 109)' }}>
                    New action
                  </Button>
                </Grid>
                <Grid item xs={12} sx={{ paddingLeft: 0 }}>
                  <InputField
                    fullWidth
                    placeholder='Search for an action'
                    name="search"
                    onChange={(e) => setSearchedString(e.target.value)}
                    type="text"
                    value={searchedString}
                  />
                </Grid>
                <Grid item xs={12}>
                  <List>
                    {items?.map((u) => (
                      <ListItem disablePadding sx={{ backgroundColor: "background.default", marginBottom: 1, borderRadius: 1, padding: 0, fontSize: "10px" }}>
                        <ButtonGroup
                          variant="contained"
                          ref={anchorRef}
                          aria-label="Button group with a nested menu"
                          fullWidth
                          sx={{ padding: 0, fontSize: "10px", backgroundColor: 'white' }}
                          color='inherit'
                        >
                          <Button onClick={() => dispatch(openMoveDialog({ element: u, isEditabled: true, isDuplicated: false }))} sx={{ backgroundColor: 'white', padding: '10px', textAlign: 'left' }}><span style={{ padding: '10px', width: '100%' }}>{u.name}</span></Button>
                          <Button
                            size="small"
                            aria-controls={open ? 'split-button-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-label="select merge strategy"
                            aria-haspopup="menu"
                            style={{ width: '1%' }}
                            sx={{ backgroundColor: 'white' }}
                          // onClick={handleToggle}
                          >
                            {/* <FontAwesomeIcon icon={faEllipsis as IconProp} /> */}
                            <TableItemMenu
                              onView={() => dispatch(openMoveDialog({ element: u, isEditabled: false, isDuplicated: false }))}
                              onEdit={() => dispatch(openMoveDialog({ element: u, isEditabled: true, isDuplicated: false }))}
                              onDuplicate={() => dispatch(openMoveDialog({ element: u, isEditabled: true, isDuplicated: true }))}
                              onDelete={async () => {
                                await dispatch(moveDelete(u.id));
                                getItems();
                              }}
                            />
                          </Button>
                        </ButtonGroup>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </Grid>
          )}
          {showList && showConcerningsPanel && (
            <MoveConcerningsPanel height={height} onHide={() => setShowConcerningsPanel(false)} />
          )}
        </Grid>
      </DialogContent>
      <Divider />
      {/* <DialogActions>
        <Button
          color="primary"
          size="small"
          onClick={onClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          color="success"
          size="small"
          disabled={(move.moveIsLoading || !move.isEditabled || (move.name.length < 3))}
          onClick={() => { handleSubmit(); }}
          variant="contained"
        >
          {(move.moveId !== null && move.moveId !== undefined) ? t('update') : t('create')}
        </Button>
      </DialogActions> */}
      <ConfirmationDialog
        message={t('do_you_want_to_close_without_saving')}
        onCancel={() => { setOpenConfirmationDialog(false) }}
        onConfirm={() => {
          setOpenConfirmationDialog(false);
          onClose();
        }}
        open={openConfirmationDialog}
        title={t('confirmation_title')}
        variant='warning'
      />
    </Dialog>
  );
};

MoveDialog.defaultProps = {
  open: false,
  stacked: false
};

MoveDialog.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  stacked: PropTypes.bool
};
